//@flow
import React, { Component } from 'react';
import { createRefetchContainer, graphql, fetchQuery } from 'react-relay';
import { injectIntl } from 'react-intl';
import get from 'lodash.get'; // TODO dibs-object-getter
import { OverlaySpinner } from 'dibs-elements/exports/OverlaySpinner';

import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { trackPageView } from '../shared/helpers/tracking';

import { PhotoRootQuery } from './PhotoRootQuery';

import { logError } from '../helpers';
import { PhotoContainer } from './content/PhotoContainer';
import RailContainer from './rail/RailContainer';

import getImageSizeParam from './helpers/getImageSizeParam';

import { interiorPhotoMessages } from './messages';
import styles from './InteriorPhotoWrapper.scss';

import type { InteriorPhotoWrapper_viewer as Viewer } from './__generated__/InteriorPhotoWrapper_viewer.graphql';
import type { RelayRefetchProp } from 'react-relay';
import type { IntlShape } from 'react-intl';

export type ProjectPhoto = {|
    +url: ?string,
    +serviceId: ?string,
    +path: ?string,
    +width: ?number,
    +height: ?number,
|};

type Props = {
    viewer?: ?Viewer,
    isClient: boolean,
    relay?: RelayRefetchProp,
    intl: IntlShape,
    isModal?: boolean,
    initialLookupUrl: string,
    photoUrls?: Array<?string>,
    photos?: $ReadOnlyArray<?ProjectPhoto>,
    onCloseModal?: () => void,
};

type State = {
    currentLookupUrl: string,
    nextLookupUrl: string,
    photos: Array<?ProjectPhoto>,
    startingPath: ?string,
    isLoading: boolean,
};

export const InteriorPhotoWrapperQuery = graphql`
    query InteriorPhotoWrapperQuery($projectRootId: String!) {
        viewer {
            designProjectRootSearch(projectRootId: $projectRootId) {
                tradePhotos {
                    url
                    serviceId
                    path
                    width
                    height
                }
            }
        }
    }
`;

export class InteriorPhotoWrapperComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            currentLookupUrl: props.initialLookupUrl,
            nextLookupUrl: '',
            photos: [],
            startingPath: null,
            isLoading: false,
            isClient: false,
        };
    }

    componentDidMount() {
        trackPageView();

        let startingPath = null;
        if (window) {
            startingPath = window.location.pathname;
            const url =
                (this.props.initialLookupUrl ||
                    get(this.props, 'viewer.tradePhotosQuery.edges.0.node.photo.url')) +
                window.location.search;

            if (this.props.isModal && this.props.initialLookupUrl) {
                window.history.pushState(url, this.getTitleString(), url);
            } else if (!history.state) {
                window.history.replaceState(url, this.getTitleString(), url);
            }

            window.onpopstate = this.handlePopState;
        }
        this.setState({ isClient: true });

        const projectRootId = parseInt(
            get(this.props, 'viewer.tradePhotosQuery.edges.0.node.projectRootId')
        );

        const { photoUrls = [], photos = [] } = this.props;

        // if the embedding page supplied its own URL & photo objects, then
        // don't fetch additional info
        if (photoUrls.length && photos.length && !this.props.isModal) {
            const _photos = photos.slice();
            this.setState({ photos: _photos, startingPath });
        } else if (projectRootId > 0 && this.props.relay) {
            const { environment } = this.props.relay;
            const vars = { projectRootId: projectRootId + '' };

            // using a promise here so `componentDidMount` can finish quickly without blocking on the project read
            fetchQuery(environment, InteriorPhotoWrapperQuery, vars).then(result => {
                const _photos = get(result, 'viewer.designProjectRootSearch.tradePhotos') || [];
                this.setState({ photos: _photos, startingPath }, this.refetchPhotoData);
            });
        } else {
            this.setState({ startingPath });
        }
    }

    componentWillUnmount() {
        window.onpopstate = null;
    }

    refetchPhotoData() {
        const { photos } = this.state;
        if (!photos.length) {
            return;
        }

        const urlLabels = photos.map(({ url }) => url);
        if (!this.props.relay) {
            return;
        }
        this.props.relay.refetch({ urlLabels }, null, (error: ?Error) => {
            if (error) {
                logError(error);
            }
            this.prefetchPhotos();

            const { isLoading, nextLookupUrl } = this.state;
            if (isLoading && nextLookupUrl) {
                this.setState({
                    isLoading: false,
                    currentLookupUrl: nextLookupUrl,
                    nextLookupUrl: '',
                });
            }
        });
    }

    prefetchPhotos = () => {
        const { photos, currentLookupUrl } = this.state;
        const currentIndex = photos.findIndex(photo => {
            return photo && photo.url === currentLookupUrl;
        });
        let nextIndex = currentIndex + 1;
        let prevIndex = currentIndex - 1;
        if (nextIndex >= photos.length) {
            nextIndex = 0;
        }

        if (prevIndex < 0) {
            prevIndex = photos.length - 1;
        }

        const lookupPhotos = [];

        if (photos[prevIndex]) {
            lookupPhotos.push(photos[prevIndex]);
        }

        lookupPhotos.push(photos[currentIndex]);

        if (photos[nextIndex] && photos[nextIndex].url) {
            lookupPhotos.push(photos[nextIndex]);
        }

        lookupPhotos.forEach(photo => {
            if (!photo || !photo.path) {
                return;
            }
            const img = new Image();
            const imageSizeParam = getImageSizeParam(photo);
            img.src = `${photo.path || ''}${imageSizeParam}`;
        });
    };

    getPhotoEdge = historyState =>
        (get(this.props, 'viewer.tradePhotosQuery.edges') || []).find(
            _edge => get(_edge, 'node.photo.url') === historyState
        );

    handlePopState = (): void => {
        const { startingPath } = this.state;
        const historyState = window.history.state;
        const isPhotosPath = /^\/photos(\/)?/.test(window.location.pathname);
        const edge = this.getPhotoEdge(historyState);
        if (isPhotosPath || (historyState && !edge)) {
            // if moving back to /photos/ search/browse page
            window.location = window.location.href;
        } else if (historyState && edge) {
            // if photo state exists and photo edge present, use that...
            this.pageChange(historyState, true);
        } else if (startingPath) {
            // ...otherwise, refresh back to your source
            window.location = startingPath + window.location.search;
        }
    };

    pageChange = (payload: string, fromPopStateEvent: ?boolean): void => {
        const photoLoaded = !!this.getPhotoEdge(payload);
        if (photoLoaded) {
            this.setState({ currentLookupUrl: payload }, this.prefetchPhotos);
        } else {
            this.setState({ isLoading: true, nextLookupUrl: payload });
        }

        if (window && payload && !fromPopStateEvent) {
            const url = payload + window.location.search;
            window.history.pushState(url, this.getTitleString(), url);
        }
    };

    closeModal = (): void => {
        if (this.props.onCloseModal) {
            this.props.onCloseModal();
        }
    };

    getTitleString = (): ?string => {
        const edge = this.getPhotoEdge(this.state.currentLookupUrl);
        if (!edge) {
            return null;
        }

        const { intl } = this.props;
        const photoName = get(edge, 'node.photo.name');
        const firmName = get(edge, 'node.firmName');
        let title;

        if (!firmName && !photoName) {
            title = intl.formatMessage(interiorPhotoMessages.titleEmpty);
        } else if (!firmName) {
            title = intl.formatMessage(interiorPhotoMessages.titleNoFirm, { photoName });
        } else {
            title = intl.formatMessage(interiorPhotoMessages.titleFull, { photoName, firmName });
        }
        return `${title} 1stdibs`;
    };

    render() {
        const { photos, currentLookupUrl, isLoading } = this.state;
        const edge = this.getPhotoEdge(currentLookupUrl);
        const node = get(edge, 'node');
        const tradePhoto = get(node, 'photo');

        const rootStyle = this.props.isModal ? styles.modalWrapper : styles.galleryWrapper;

        return (
            <div
                id="interiorPhotosContainer"
                className="interiorPhotosContainer primary-viewport-width"
            >
                <div id="interiorPhotoContent" className="interiorPhotoContent">
                    <div className={rootStyle}>
                        {this.props.isModal && <ModalCloseButton onClick={this.closeModal} />}
                        <PhotoContainer
                            isClient={this.props.isClient}
                            tradePhoto={(tradePhoto: ?Object)}
                            project={null}
                            pageChange={this.pageChange}
                            photoQuery={(node: ?Object)}
                            photos={photos}
                        />
                        <RailContainer
                            isClient={this.props.isClient || false}
                            tradePhoto={(tradePhoto: ?Object)}
                            photoQuery={(node: ?Object)}
                        />
                    </div>
                </div>
                <OverlaySpinner isOpen={isLoading} onClose={() => {}} />
            </div>
        );
    }
}

export const InteriorPhotoWrapper = createRefetchContainer(
    // $FlowFixMe
    injectIntl(InteriorPhotoWrapperComponent),
    {
        viewer: graphql`
            fragment InteriorPhotoWrapper_viewer on Viewer {
                tradePhotosQuery(urlLabels: $urlLabels) {
                    edges {
                        node {
                            ...RailContainer_photoQuery
                            ...PhotoContainer_photoQuery
                            projectId
                            projectRootId
                            firmName
                            photo {
                                ...PhotoContainer_tradePhoto
                                ...RailContainer_tradePhoto
                                name
                                url
                                serviceId
                            }
                        }
                    }
                }
            }
        `,
    },
    PhotoRootQuery
);
